import { useEffect, useState } from 'react';

export const PHONE_MEDIA_QUERY = '(max-width: 767px)';

const SAFE_TOP = 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))';
const SAFE_BOTTOM = 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))';
/** Above the native tab bar (its height already counts the safe area), else above the system bar. */
export const SHEET_BOTTOM = `calc(max(var(--native-tab-bar-h, 0px), ${SAFE_BOTTOM}) + 8px)`;
export const SHEET_TOP = `calc(${SAFE_TOP} + 8px)`;
export const SHEET_MAX_HEIGHT = `calc(100dvh - ${SHEET_BOTTOM} - ${SHEET_TOP})`;
export const CENTERED_MAX_HEIGHT = `calc(100dvh - 2 * ${SHEET_BOTTOM})`;

function matches(query: string, fallback: boolean): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return fallback;
  return window.matchMedia(query).matches;
}

export function isPhoneViewport(): boolean {
  return matches(PHONE_MEDIA_QUERY, false);
}

/** Touch screens cannot hover: a "hover the highlight" step must not block them. */
export function canHover(): boolean {
  return matches('(hover: hover)', true);
}

export function useIsPhoneViewport(): boolean {
  const [isPhone, setIsPhone] = useState(isPhoneViewport);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(PHONE_MEDIA_QUERY);
    const onChange = () => setIsPhone(query.matches);
    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return isPhone;
}

export interface SheetOffsets {
  top: number;
  bottom: number;
}

/**
 * Pixel size of the panel's top and bottom offsets (safe areas, tab bar), measured per step and
 * on resize only: measuring touches the DOM, which the spotlight's mutation observer watches.
 */
export function useSheetOffsets(enabled: boolean, stepId: string): SheetOffsets {
  const [offsets, setOffsets] = useState<SheetOffsets>({ top: 0, bottom: 0 });
  useEffect(() => {
    if (!enabled) return;
    const measure = () => {
      const next = { top: cssLengthToPx(SHEET_TOP), bottom: cssLengthToPx(SHEET_BOTTOM) };
      setOffsets((prev) => (prev.top === next.top && prev.bottom === next.bottom ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [enabled, stepId]);
  return offsets;
}

/** Resolves a CSS length (safe-area vars, calc) to pixels, as the browser lays it out. */
export function cssLengthToPx(length: string): number {
  const probe = document.createElement('div');
  probe.style.cssText = `position:fixed;visibility:hidden;pointer-events:none;top:0;height:${length}`;
  document.body.appendChild(probe);
  const px = probe.getBoundingClientRect().height;
  probe.remove();
  return px;
}
